import { useContext, useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import DropDownViewModel from '../Utilities/ViewModels/DropDownViewModel';
import { APIContext } from '../Contexts/APIContext';

type ManagerUserChange = {
    id: string;
    userName: string;
    userEmail: string;
    changedByName: string;
    organisationName?: string | null;
    siteName?: string | null;
    changeType: string;
    timestamp: string;
};

type PromptFilterOptions = {
    organisations: DropDownViewModel[];
    sites: DropDownViewModel[];
};

export default function ManagerUserChangesPage() {
    const { get } = useContext(APIContext);
    const [rows, setRows] = useState<ManagerUserChange[]>([]);
    const [filters, setFilters] = useState<PromptFilterOptions>({ organisations: [], sites: [] });
    const [selectedOrganisation, setSelectedOrganisation] = useState<DropDownViewModel | null>(null);
    const [selectedSite, setSelectedSite] = useState<DropDownViewModel | null>(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [showReassignments, setShowReassignments] = useState(false);

    useEffect(() => {
        get('PromptAdmin', 'GetManagerUserChangeFilters', setFilters);
    }, []);

    useEffect(() => {
        const organisationId = selectedOrganisation?.id ? `organisationId=${selectedOrganisation.id}` : '';
        const siteId = selectedSite?.id ? `siteId=${selectedSite.id}` : '';
        const from = fromDate ? `from=${fromDate}` : '';
        const to = toDate ? `to=${toDate}` : '';
        const includeReassignments = `includeReassignments=${showReassignments}`;
        const query = [organisationId, siteId, from, to, includeReassignments].filter(Boolean).join('&');
        get('PromptAdmin', `GetManagerUserChanges?${query}`, setRows);
    }, [selectedOrganisation, selectedSite, fromDate, toDate, showReassignments]);

    const siteOptions = useMemo(() => {
        if (!selectedOrganisation) {
            return filters.sites;
        }

        return filters.sites.filter((site) => site.data === selectedOrganisation.id);
    }, [filters.sites, selectedOrganisation]);

    return (
        <Paper sx={{ p: 2 }}>
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Typography variant="h4">Manager User Changes</Typography>
                </Grid>
                <Grid size={4}>
                    <Autocomplete
                        options={filters.organisations}
                        value={selectedOrganisation}
                        onChange={(_, value) => {
                            setSelectedOrganisation(value);
                            setSelectedSite(null);
                            get('PromptAdmin', `GetManagerUserChangeFilters${value?.id ? `?organisationId=${value.id}` : ''}`, setFilters);
                        }}
                        renderInput={(params) => <TextField {...params} label="Organisation" />}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                </Grid>
                <Grid size={4}>
                    <Autocomplete
                        options={siteOptions}
                        value={selectedSite}
                        onChange={(_, value) => setSelectedSite(value)}
                        renderInput={(params) => <TextField {...params} label="Site" />}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                </Grid>
                <Grid size={2}>
                    <TextField
                        label="From"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={fromDate}
                        onChange={(event) => setFromDate(event.target.value)}
                    />
                </Grid>
                <Grid size={2}>
                    <TextField
                        label="To"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={toDate}
                        onChange={(event) => setToDate(event.target.value)}
                    />
                </Grid>
                <Grid size={12}>
                    <FormControlLabel
                        control={<Checkbox checked={showReassignments} onChange={(event) => setShowReassignments(event.target.checked)} />}
                        label="Show Reassignments"
                    />
                </Grid>
                <Grid size={12}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Changed By</TableCell>
                                    <TableCell>Organisation</TableCell>
                                    <TableCell>Site</TableCell>
                                    <TableCell>Change Type</TableCell>
                                    <TableCell>Timestamp</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.userName}</TableCell>
                                        <TableCell>{row.userEmail}</TableCell>
                                        <TableCell>{row.changedByName}</TableCell>
                                        <TableCell>{row.organisationName ?? ''}</TableCell>
                                        <TableCell>{row.siteName ?? ''}</TableCell>
                                        <TableCell>{row.changeType}</TableCell>
                                        <TableCell>{new Date(row.timestamp).toLocaleString('en-GB')}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Paper>
    );
}
